/**
 * Helpers for Strapi v5 REST shapes.
 *
 * - Content-type relations expect `documentId` values (often via `connect`).
 *   See https://docs.strapi.io/cms/api/rest/relations
 * - Media fields still expect the numeric upload `id`. `connect` is *not* valid
 *   for media. See https://docs.strapi.io/cms/api/rest/upload (and issue
 *   strapi/strapi#24035).
 */

type StrapiUploadFile = {
  id?: number | string
  documentId?: string
}

function readFirstUploadEntry(body: unknown): StrapiUploadFile | undefined {
  if (body == null) return undefined
  const list = Array.isArray(body) ? body : (body as { data?: unknown }).data
  if (!Array.isArray(list) || list.length === 0) return undefined
  return list[0] as StrapiUploadFile
}

/** Numeric upload id (preferred for media fields in v5). */
export function getStrapiUploadFirstFileMediaId(
  body: unknown
): number | string | undefined {
  const file = readFirstUploadEntry(body)
  return file?.id ?? file?.documentId
}

/**
 * Backwards-compatible alias kept for non-media flows that still want a
 * stable identifier. Prefers `id` so it stays useful for media too.
 */
export const getStrapiUploadFirstFileId = getStrapiUploadFirstFileMediaId

export function getStrapiCreateResponseEntryId(
  body: unknown
): string | number | undefined {
  if (body == null || typeof body !== 'object') return undefined
  const data = (body as { data?: unknown }).data
  if (data == null || typeof data !== 'object') return undefined
  const doc = data as { documentId?: string; id?: number | string }
  return doc.documentId ?? doc.id
}

/** Strapi v5 content-type relation: `{ connect: [documentId] }`. Do NOT use for media. */
export function strapiRelationConnect(documentIdOrRef: string | number): {
  connect: string[]
} {
  return { connect: [String(documentIdOrRef)] }
}
